import React from 'react';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { colors, layout } from '../../design-system/tokens';

const count = (value) => Number(value || 0).toLocaleString('ko-KR');

const Step = ({ number, title, detail, status, complete, action, onClick }) => {
  const tone = complete ? colors.status.success : colors.status.info;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '32px minmax(0, 1fr)',
        gap: 1.25,
        alignItems: 'start',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: 28,
          height: 28,
          display: 'grid',
          placeItems: 'center',
          borderRadius: '50%',
          bgcolor: tone.bg,
          color: tone.fg,
          fontWeight: 900,
          fontSize: 13,
        }}
      >
        {number}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={0.75}
          sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
          <Chip
            size="small"
            label={status}
            sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' }, bgcolor: tone.bg, color: tone.fg }}
          />
        </Stack>
        <Typography variant="body2" sx={{ mt: 0.4, color: colors.text.secondary }}>
          {detail}
        </Typography>
        {action && onClick && (
          <Button size="small" onClick={onClick} sx={{ mt: 0.75, px: 0 }}>
            {action} →
          </Button>
        )}
      </Box>
    </Box>
  );
};

/** 첫 가맹 학원이 반 생성부터 수동 출결까지 놓치지 않도록 하는 안내 카드. */
const AcademyPilotGuide = ({ summary, canReadParents, canUseAttendance, onNavigate }) => {
  const activeClasses = Number(summary?.activeClasses || 0);
  const activeStudents = Number(summary?.students?.active || 0);
  const unlinkedStudents = Number(summary?.guardian?.unlinkedStudents || 0);
  const pendingRequests = Number(summary?.guardian?.pendingRequests || 0);
  const hasClasses = activeClasses > 0;
  const hasStudents = activeStudents > 0;

  return (
    <Paper sx={{ p: { xs: 2, sm: layout.cardPadding }, mb: layout.sectionGap }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
        첫 파일럿 운영 순서
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, color: colors.text.secondary }}>
        반 생성부터 보호자 수락 확인, 수동 출결 기록까지 아래 순서로 진행하세요. 실제 청구와
        환불 실행은 이 흐름에 포함되지 않습니다.
      </Typography>

      <Stack spacing={2.25} sx={{ mt: 2 }}>
        <Step
          number="1"
          title="반 만들기"
          status={hasClasses ? `운영 반 ${count(activeClasses)}개` : '먼저 시작'}
          complete={hasClasses}
          detail={hasClasses ? '수업 관리에서 일정과 담당 정보를 확인하세요.' : '수업 일정과 교습비를 입력해 반을 먼저 등록하세요.'}
          action={hasClasses ? '반 관리 열기' : '반 추가하기'}
          onClick={() => onNavigate('/owner-v2/classes')}
        />
        <Step
          number="2"
          title="원생 등록 및 반 배정"
          status={hasStudents ? `재원생 ${count(activeStudents)}명` : '등록 필요'}
          complete={hasStudents}
          detail={
            hasStudents
              ? '원생의 반 배정과 보호자 연락처 입력 여부를 확인하세요.'
              : hasClasses
                ? '원생 이름·학년·수강 반을 등록하고 보호자 연락처를 입력하세요.'
                : '반을 만든 뒤 원생을 등록하고 반을 배정하세요.'
          }
          action="수강생 관리 열기"
          onClick={() => onNavigate('/owner-v2/students')}
        />
        {canReadParents && (
          <Step
            number="3"
            title="보호자 초대와 수락 확인"
            status={
              !hasStudents
                ? '원생 등록 후 진행'
                : pendingRequests > 0
                  ? `수락 대기 ${count(pendingRequests)}건`
                  : unlinkedStudents > 0
                    ? `연결 요청 필요 ${count(unlinkedStudents)}명`
                    : '연결 확인 완료'
            }
            complete={hasStudents && unlinkedStudents === 0 && pendingRequests === 0}
            detail={
              pendingRequests > 0
                ? '보호자가 앱에서 수락할 때까지 기다리세요. 만료되면 연락처를 확인한 뒤 다시 요청합니다.'
                : unlinkedStudents > 0
                  ? '보호자 연락처를 확인하고 앱 연결 요청을 보내세요. 보호자의 회원 존재 여부는 표시하지 않습니다.'
                  : '연결 상태를 확인한 뒤 다음 단계로 진행하세요.'
            }
            action="보호자 연결 상태 보기"
            onClick={() => onNavigate('/owner-v2/parents')}
          />
        )}
        {canUseAttendance && (
          <Step
            number={canReadParents ? '4' : '3'}
            title="수동 출결 기록"
            status={hasStudents ? '기록 시작' : '원생 등록 후 진행'}
            complete={false}
            detail={
              hasStudents
                ? '반과 날짜를 고른 뒤 출석·지각·결석 등을 기록하고 저장하세요. 미기록은 결석이 아닙니다.'
                : '반에 재원생을 배정하면 해당 반의 출결을 기록할 수 있어요.'
            }
            action="출결 관리 열기"
            onClick={() => onNavigate('/owner-v2/attendance')}
          />
        )}
      </Stack>
    </Paper>
  );
};

export default AcademyPilotGuide;
